import { Lock } from '@phosphor-icons/react';
import { ReactNode } from 'react';
import { AccentButton } from '../../../../../../shared/Buttons';

// Shared shell for the CustomerHome action cards (Find Me Freight, Request a
// Rental). They sit side by side at half the column on desktop and stack full
// width on tablet and mobile, where half the column is too narrow for the row.
// One row — icon, title + subtitle, CTA on the right — to keep the cards short.
// `flex-wrap` lets the CTA drop under the text when even the full-width mobile
// card can't fit all three, instead of squeezing the copy to one word per line,
// and once it's on a line of its own the CTA stretches to the card's width.
// Square corners, like the Balances and Savings widgets it sits between.

interface HomeActionCardProps {
  /** Prefix for the card's test ids, e.g. `requestLoad` → `requestLoad:card`. */
  testIdPrefix: string;
  icon: ReactNode;
  title: string;
  subtitle: string;
  ctaIcon: ReactNode;
  ctaLabel: string;
  onClick: () => void;
  /** Shows the "NEW" badge beside the title. */
  isNew?: boolean;
  /** When set, the card is locked: this replaces the subtitle, the CTA is
   *  disabled and its icon becomes a lock. */
  lockedReason?: string;
}

const HomeActionCard = ({
  testIdPrefix,
  icon,
  title,
  subtitle,
  ctaIcon,
  ctaLabel,
  onClick,
  isNew = false,
  lockedReason,
}: HomeActionCardProps) => (
  <div
    data-testid={`${testIdPrefix}:card`}
    className="flex h-full w-full border border-bordergray bg-white p-3"
  >
    <div
      data-testid={`${testIdPrefix}:cardRow`}
      className="flex w-full flex-row flex-wrap items-center gap-3"
    >
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded border border-bordergray bg-primarybg">
        {icon}
      </div>
      {/* min-w keeps the text block from collapsing before the row wraps.
          Text and CTA both grow, but at 999:1 — so while they share the row the
          text takes the free space and the CTA stays at its natural width, and
          when the CTA wraps onto its own line it grows to fill it. */}
      <div className="min-w-[120px] grow-[999] basis-0">
        <div className="flex flex-row items-center gap-2">
          <p className="text-sm font-semibold text-primary">{title}</p>
          {isNew && (
            <span
              data-testid={`${testIdPrefix}:newBadge`}
              className="shrink-0 rounded-full bg-bgyellow px-2 py-0.5 text-[11px] font-bold text-accent"
            >
              NEW
            </span>
          )}
        </div>
        <p
          data-testid={`${testIdPrefix}:subtitle`}
          className="text-xs text-graysecondary"
        >
          {lockedReason ?? subtitle}
        </p>
      </div>

      <AccentButton
        onClick={onClick}
        disabled={lockedReason !== undefined}
        addClass="grow shrink-0 rounded-full px-4 !py-2 !text-[14px] disabled:!opacity-50"
      >
        <div className="flex flex-row items-center justify-center gap-1.5">
          {lockedReason !== undefined ? (
            <Lock size={16} weight="fill" />
          ) : (
            ctaIcon
          )}
          <span>{ctaLabel}</span>
        </div>
      </AccentButton>
    </div>
  </div>
);

export default HomeActionCard;
